import React, { useState, useEffect, useRef } from 'react'
import { Flex } from '../../../Components/Common/Flex'
import { Spinner } from '../../../Components/Common/Spinner'
import { Button } from '../../../Components/Common/Button'
import { useParams, useNavigate } from 'react-router-dom'
import { FC } from '../.././../Services'
import { getLabel, getGroupedFields } from '../Draggable/Functions'
import { FloatInput } from '../../../Components/Common/FloatInput'
import { Modal } from '../../../Components/Common/Modal'
import { Text } from '../../../Components/Common/Text'
import { invalidateQuery } from '../../../Services/QueryCache'
import { Checkbox } from 'primereact/checkbox'

const DataField = ({ label, value, onCopy, isCopied }) => {
  return (
    <div
      style={{
        height: '100%',
        padding: '12px',
        marginBottom: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#efefef'
        e.currentTarget.style.borderColor = '#d0d0d0'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5'
        e.currentTarget.style.borderColor = '#e0e0e0'
      }}
      onClick={() => onCopy(label, value)}
      title='Clicca per copiare il valore'
    >
      <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>
        {getLabel(label)}
      </div>
      <div
        style={{
          fontSize: '13px',
          color: '#333',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          backgroundColor: '#fff',
          padding: '8px',
          borderRadius: '3px',
          minHeight: '20px'
        }}
      >
        {String(value)}
      </div>
      {isCopied && (
        <div style={{ fontSize: '11px', color: '#4caf50', marginTop: '4px', fontWeight: '500' }}>
          ✓ Copiato
        </div>
      )}
    </div>
  )
}

const DataSection = ({ title, fields, onCopy, copiedKey }) => {
  const categoryBorders = {
    'Informazioni Hotel': '#1976d2',
    Ospite: '#7b1fa2',
    Date: '#388e3c',
    Servizi: '#f57c00',
    Pagamento: '#c2185b',
    Società: '#558b2f',
    Altro: '#666'
  }

  const borderColor = categoryBorders[title] || '#666'

  return (
    <div style={{}}>
      <h4
        style={{
          marginTop: 0,
          marginBottom: '12px',
          fontSize: '13px',
          fontWeight: '700',
          color: borderColor,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          paddingLeft: '8px',
          borderLeft: `3px solid ${borderColor}`
        }}
      >
        {title}
      </h4>
      <Flex row wrap js style={{ alignItems: 'stretch' }}>
        {Object.entries(fields).map(([key, value]) => (
          <div style={{ width: '33%', display: 'flex', flexDirection: 'column' }} key={key}>
            <DataField label={key} value={value} onCopy={onCopy} isCopied={copiedKey === key} />
          </div>
        ))}
      </Flex>
    </div>
  )
}

export const CompileWebForm = () => {
  const { webFormId, environmentId } = useParams()
  const [form, setForm] = useState({})
  const [iframeError, setIframeError] = useState(false)
  const [isCheckingEmbeddable, setIsCheckingEmbeddable] = useState(false)
  const [normalizedUrl, setNormalizedUrl] = useState('')
  const [copiedKey, setCopiedKey] = useState(null)
  const copyTimerRef = useRef(null)
  const navigate = useNavigate()
  const originalUrlRef = useRef('')

  const [modalVisible, setModalVisible] = useState(false)

  const handleCopy = (label, value) => {
    try {
      navigator.clipboard.writeText(String(value))
    } catch (e) {
      // fallback: ignore
    }
    setCopiedKey(label)
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
    copyTimerRef.current = setTimeout(() => setCopiedKey(null), 2000)
  }

  useEffect(() => {
    if (webFormId) {
      FC.service('formsWeb').find({ query: { id: webFormId } }).then(([res]) => {
        setForm(res)
        originalUrlRef.current = res.url || ''
      })
    }
  }, [webFormId])

  // Auto-save URL on component unmount or when URL changes
  useEffect(() => {
    return () => {
      if (form?.id && form.url !== originalUrlRef.current) {
        FC.service('formsWeb').patch(form.id, { url: form.url })
          .catch((err) => console.error('Errore nel salvataggio automatico URL:', err))
      }
    }
  }, [form?.id, form?.url])

  // Preflight check: verifica se l'URL è embeddabile
  useEffect(() => {
    let active = true

    async function checkUrl () {
      if (!form?.url) {
        setIsCheckingEmbeddable(false)
        setIframeError(false)
        setNormalizedUrl('')
        return
      }

      // Se l'URL non ha protocollo, aggiungilo
      let urlToCheck = form.url
      if (!form.url.match(/^https?:\/\//i)) {
        urlToCheck = `https://${form.url}`
      }

      // Valida il formato dell'URL prima di chiamare l'API
      let isValidUrl = false
      try {
        // eslint-disable-next-line no-new
        new URL(urlToCheck)
        isValidUrl = true
      } catch (err) {
        // URL non valido, non chiamare l'API
        isValidUrl = false
      }

      if (!isValidUrl) {
        setIsCheckingEmbeddable(false)
        setIframeError(true) // Marca come errore per non renderizzare l'iframe
        setNormalizedUrl('')
        return
      }

      // Salva l'URL normalizzato
      setNormalizedUrl(urlToCheck)
      setIsCheckingEmbeddable(true)
      setIframeError(false)

      try {
        const response = await FC.service('public').get('check-embeddable', {
          query: { url: form.url }
        })

        if (!active) return

        if (!response.ok) {
          setIframeError(true)
        }
      } catch (err) {
        if (!active) return
        console.error('Errore nel check embeddable:', err)
        // In caso di errore nel check, assumiamo che non sia embeddable
        setIframeError(true)
      } finally {
        if (active) {
          setIsCheckingEmbeddable(false)
        }
      }
    }

    checkUrl()

    return () => {
      active = false
    }
  }, [form?.url])

  const renderFormData = () => {
    if (form?.data && Object.keys(form.data).length > 0) {
      const groupedData = getGroupedFields(form.data)
      return (
        <div>
          <Flex style={{ height: 60 }}>
            <h3 style={{ marginTop: 0, fontSize: '14px', fontWeight: '600' }}>
              Dati del Documento
            </h3>
          </Flex>
          {Object.entries(groupedData).map(([category, fields]) => (
            <DataSection
              key={category}
              title={category}
              fields={fields}
              onCopy={handleCopy}
              copiedKey={copiedKey}
            />
          ))}
        </div>
      )
    }
    return (
      <div style={{ color: '#999', fontSize: '13px' }}>
        Nessun dato disponibile
      </div>
    )
  }

  if (!form?.id) return <Spinner />

  return (
    <Flex fw row js as style={{ height: '100vh' }}>
      <Flex style={{ width: '40%', overflowY: 'auto', paddingRight: '12px' }}>
        {renderFormData()}
      </Flex>
      <Flex style={{ width: '60%', display: 'flex', flexDirection: 'column' }} js>
        <Flex fw row style={{ height: 60, flexShrink: 0 }} s>
          <FloatInput
            value={[form?.url || '', (data) => setForm({ ...form, url: data })]}
            style={{ width: '33.3%', marginRight: 10 }} label='URL Form'
          />
          <Flex row style={{ marginLeft: 20 }}>
            <Text value='Completato: ' style={{ marginRight: 10 }} />
            <Checkbox
              checked={form?.status === 'compiled'}
              onChange={() => setModalVisible(true)}
            />
          </Flex>
          <Modal visible={modalVisible} header='Completamento Form' onHide={() => setModalVisible(false)}>
            <Flex fw>
              <Text value={`Vuoi segnare questo form come ${form.status !== 'compiled' ? 'completato' : 'non completato'}?`} />
              <Flex fw row jb style={{ marginTop: 20 }}>
                <Button label='Annulla' icon='fa-duotone fa-times' onClick={() => setModalVisible(false)} />
                <Button
                  label='Conferma' icon='fa-duotone fa-check' onClick={async () => {
                    if (form) {
                      const newStatus = form.status !== 'compiled' ? 'compiled' : 'created'
                      try {
                        const updated = await FC.service('formsWeb').patch(form.id, { status: newStatus })
                        setForm(updated)
                        window.growl.show({ severity: 'success', summary: 'Success', detail: 'Form completato' })
                        invalidateQuery(['formsWeb'])
                        if (newStatus === 'compiled') {
                          navigate('/environments/' + environmentId + '/webForm/list')
                        }
                      } catch (err) {
                        console.error('Errore nell\'aggiornamento dello status:', err)
                        window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Impossibile completare il form' })
                      }
                    }
                    setModalVisible(false)
                  }}
                />
              </Flex>
            </Flex>
          </Modal>
        </Flex>
        {form?.url && (
          iframeError
            ? (
              <div style={{
                padding: '16px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                color: '#856404',
                marginTop: '12px'
              }}
              >
                <p style={{ marginTop: 0, fontSize: '13px', fontWeight: '600' }}>
                  Impossibile caricare il form in questa pagina
                </p>
                <p style={{ fontSize: '12px', margin: '8px 0' }}>
                  Il sito web non consente di essere visualizzato in un iframe per motivi di sicurezza (Content Security Policy).
                </p>
                <a href={form.url} target='_blank' rel='noopener noreferrer' style={{ color: '#0066cc', textDecoration: 'underline', fontSize: '13px' }}>
                  Apri il form in una nuova finestra →
                </a>
              </div>
              )
            : isCheckingEmbeddable
              ? (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #90caf9',
                  borderRadius: '4px',
                  color: '#1565c0',
                  textAlign: 'center',
                  marginTop: '12px'
                }}
                >
                  <p style={{ marginTop: 0, fontSize: '13px', fontWeight: '600' }}>
                    Verifica disponibilità del form...
                  </p>
                </div>
                )
              : (
                <iframe
                  src={normalizedUrl}
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 130px)',
                    border: 'none',
                    borderRadius: '4px',
                    flexGrow: 1
                  }}
                  title='Web Form'
                  sandbox='allow-same-origin allow-scripts allow-forms allow-popups'
                />
                )
        )}
      </Flex>
    </Flex>
  )
}
